/**
 * Admin – Das Schwarze Brett (NT-SCHWARZES-BRETT-01)
 *
 * Kanban-artige Kuratier-Ansicht: alle Vorschläge, alle Status, ein Status-Dropdown +
 * ANWENDEN-Button pro Karte (kein Drag-and-Drop, siehe NT-SCHWARZES-BRETT-ANALYSE-01 D3).
 * Kategorien wurden aus der Spieler-UI entfernt (Chris-Feedback 2026-08-30) — daher hier
 * ebenfalls nicht mehr angezeigt (Backend liefert das Feld weiterhin, ungenutzt).
 *
 * Routen (Backend):
 *   GET  /api/v1/admin/suggestions
 *   POST /api/v1/admin/suggestions/:id/status
 */

import { api, ApiError } from '../../services/api.js';
import { showToast } from '../../utils/toast.js';

type SuggestionStatus =
  | 'EINGEGANGEN' | 'IN_PRUEFUNG' | 'GEPLANT' | 'IN_ARBEIT' | 'UMGESETZT' | 'ABGELEHNT' | 'ZURUECKGESTELLT';

interface AdminSuggestion {
  id: string;
  publicNumber: number;
  title: string;
  body: string;
  category: string;
  status: SuggestionStatus;
  voteCount: number;
  rejectionReason: string | null;
  createdAt: string;
  shippedAt: string | null;
  submitterUsername: string | null;
}

const STATUS_ORDER: SuggestionStatus[] = [
  'EINGEGANGEN', 'IN_PRUEFUNG', 'GEPLANT', 'IN_ARBEIT', 'UMGESETZT', 'ABGELEHNT', 'ZURUECKGESTELLT',
];
const STATUS_LABELS: Record<SuggestionStatus, string> = {
  EINGEGANGEN: 'Eingegangen',
  IN_PRUEFUNG: 'In Prüfung',
  GEPLANT: 'Geplant',
  IN_ARBEIT: 'In Arbeit',
  UMGESETZT: 'Umgesetzt',
  ABGELEHNT: 'Abgelehnt',
  ZURUECKGESTELLT: 'Zurückgestellt',
};
let containerId = '';
let cache: AdminSuggestion[] = [];

export function initAdminSuggestions(targetContainerId: string): void {
  containerId = targetContainerId;
  const container = document.getElementById(containerId);
  if (container) container.innerHTML = `<p style="color:var(--text-muted);">LADE VORSCHLÄGE...</p>`;
  void loadSuggestions();
}

async function loadSuggestions(): Promise<void> {
  const container = document.getElementById(containerId);
  if (!container) return;

  try {
    cache = await api.get<AdminSuggestion[]>('/admin/suggestions');
    render();
  } catch (err) {
    container.innerHTML = `<p style="color:var(--accent-red);">${escapeHtml(
      err instanceof ApiError ? err.message : 'Fehler beim Laden der Vorschläge.',
    )}</p>`;
  }
}

function render(): void {
  const container = document.getElementById(containerId);
  if (!container) return;

  if (cache.length === 0) {
    container.innerHTML = `<p style="color:var(--text-muted);">NOCH KEINE VORSCHLÄGE VORHANDEN.</p>`;
    return;
  }

  container.innerHTML = `
    <div style="display:flex;gap:12px;overflow-x:auto;padding-bottom:8px;">
      ${STATUS_ORDER.map((status) => columnHtml(status)).join('')}
    </div>
  `;

  container.querySelectorAll<HTMLButtonElement>('[data-as-apply]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.asApply;
      if (id) void handleApply(id);
    });
  });

  container.querySelectorAll<HTMLSelectElement>('[data-as-status-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const id = select.dataset.asStatusSelect;
      if (!id) return;
      const reasonField = container.querySelector<HTMLDivElement>(`[data-as-reason-wrap="${id}"]`);
      if (reasonField) reasonField.style.display = select.value === 'ABGELEHNT' ? 'block' : 'none';
    });
  });
}

function columnHtml(status: SuggestionStatus): string {
  const items = cache.filter((s) => s.status === status);
  return `
    <div style="flex:0 0 300px;background:var(--bg-surface);border:1px solid var(--border);padding:10px;">
      <div style="color:var(--accent-gold);font-size:12px;letter-spacing:0.08em;margin-bottom:8px;
                  display:flex;justify-content:space-between;">
        <span>${STATUS_LABELS[status]}</span>
        <span style="color:var(--text-muted);">${String(items.length)}</span>
      </div>
      <div style="display:flex;flex-direction:column;gap:8px;">
        ${items.length === 0 ? `<p style="color:var(--text-muted);font-size:12px;">–</p>` : items.map(cardHtml).join('')}
      </div>
    </div>
  `;
}

function cardHtml(s: AdminSuggestion): string {
  const createdLabel = new Date(s.createdAt).toLocaleDateString('de-DE');
  const submitter = s.submitterUsername ?? '[Account gelöscht]';

  return `
    <div style="border:1px solid var(--border);padding:8px;font-size:12px;">
      <div style="color:var(--text-muted);display:flex;justify-content:space-between;margin-bottom:4px;">
        <span>#${String(s.publicNumber).padStart(4, '0')}</span>
        <span>▲ ${String(s.voteCount)}</span>
      </div>
      <div style="color:var(--text-primary);font-weight:bold;margin-bottom:4px;">${escapeHtml(s.title)}</div>
      <div style="color:var(--text-muted);margin-bottom:6px;max-height:60px;overflow-y:auto;white-space:pre-wrap;">${escapeHtml(s.body)}</div>
      <div style="color:var(--text-muted);margin-bottom:6px;">
        von ${escapeHtml(submitter)} · ${createdLabel}
        ${s.status === 'ABGELEHNT' && s.rejectionReason ? `<br>Grund: ${escapeHtml(s.rejectionReason)}` : ''}
      </div>
      <select data-as-status-select="${s.id}" style="${selectStyle()}margin-bottom:4px;">
        ${STATUS_ORDER.map((opt) => `<option value="${opt}"${opt === s.status ? ' selected' : ''}>${STATUS_LABELS[opt]}</option>`).join('')}
      </select>
      <div data-as-reason-wrap="${s.id}" style="display:${s.status === 'ABGELEHNT' ? 'block' : 'none'};margin-bottom:4px;">
        <input
          type="text"
          data-as-reason="${s.id}"
          placeholder="Begründung (Pflicht bei Ablehnung)"
          value="${escapeAttr(s.rejectionReason ?? '')}"
          maxlength="500"
          style="${inputStyle()}"
        />
      </div>
      <button data-as-apply="${s.id}" style="${btnStyle()}">ANWENDEN</button>
    </div>
  `;
}

async function handleApply(id: string): Promise<void> {
  const select = document.querySelector<HTMLSelectElement>(`[data-as-status-select="${id}"]`);
  const reasonInput = document.querySelector<HTMLInputElement>(`[data-as-reason="${id}"]`);
  const applyBtn = document.querySelector<HTMLButtonElement>(`[data-as-apply="${id}"]`);
  if (!select) return;

  const status = select.value as SuggestionStatus;
  const rejectionReason = reasonInput?.value.trim() ?? '';

  if (status === 'ABGELEHNT' && !rejectionReason) {
    showToast('Begründung ist bei Ablehnung Pflicht.', 'error');
    return;
  }

  if (applyBtn) applyBtn.disabled = true;
  try {
    await api.post(`/admin/suggestions/${id}/status`, {
      status,
      ...(status === 'ABGELEHNT' ? { rejectionReason } : {}),
    });
    showToast('Status aktualisiert.', 'info');
    void loadSuggestions();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Aktualisieren.', 'error');
    if (applyBtn) applyBtn.disabled = false;
  }
}

function selectStyle(): string {
  return 'width:100%;padding:6px 8px;background:var(--bg-elevated);border:1px solid var(--border);' +
    'color:var(--text-primary);font-family:\'Share Tech Mono\',monospace;font-size:11px;';
}

function inputStyle(): string {
  return 'width:100%;padding:6px 8px;background:var(--bg-elevated);border:1px solid var(--border);' +
    'color:var(--text-primary);font-family:\'Share Tech Mono\',monospace;font-size:11px;outline:none;box-sizing:border-box;';
}

function btnStyle(): string {
  return 'width:100%;border:1px solid var(--accent-green);color:var(--accent-green);background:transparent;' +
    'padding:5px;cursor:pointer;font-family:\'Share Tech Mono\',monospace;font-size:11px;letter-spacing:0.05em;';
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(str: string): string {
  return str.replace(/"/g, '&quot;');
}
